// Időkapu-foglaló · Part 2 — Factory calendar (read view).
//
// - Weekly grid: 7 columns, one per day.
// - Prev / Today / Next navigation.
// - Hover a day → floating popup listing that day's bookings (vonal · szolgáltató · időpont).
// - Click a day → expanded detail panel below the grid with the ordered booking list.
// - Fill bar per day: booked / capacity.
//
// Popup/detail styling deliberately uses CSS variables (--bg-elevated, --text) so
// contrast is correct in BOTH light and dark themes — no hardcoded dark bg.
//
// Access: tenant.dock_scheduling_enabled + any dock role (dock_admin OR dock_booker).
// Route: /dock-scheduling/calendar (added via App.js). RouteGuard gates it at route level;
// the API also enforces the tenant + role gate.

import React, { useMemo, useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CalendarClock, ChevronLeft, ChevronRight, Clock3, Package, Building2, Truck, Circle, Activity, TrendingUp, XCircle, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import ArrivalPill from "./DockArrivalPill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling/calendar`;
const BLUE = "#185FA5";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  letterSpacing: ls,
});

const DAY_NAMES_HU = ["Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat", "Vasárnap"];
const DAY_SHORT_HU = ["Hét", "Kedd", "Sze", "Csüt", "Pén", "Szo", "Vas"];

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Return the ISO date string (YYYY-MM-DD) of the Monday of the week
 *  containing the given date (local time semantics — we just shift days). */
function mondayOf(d) {
  const dt = new Date(d);
  const dow = (dt.getDay() + 6) % 7; // Monday = 0
  dt.setDate(dt.getDate() - dow);
  return isoDate(dt);
}
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(iso, days) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return isoDate(d);
}
function humanDate(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

// ─── Data hook ──────────────────────────────────────────────────────────────

function useWeek(start) {
  return useQuery({
    queryKey: ["ds-cal-week", start],
    queryFn: () => axios.get(`${API}/week`, { params: start ? { start } : {} }).then(r => r.data),
    keepPreviousData: true,
  });
}

// iter145 · B3 — Weekly utilization panel (dock_admin only). Silently 403s
// for a pure booker, which the UI treats as "hide the panel".
function useUtilization(start) {
  return useQuery({
    queryKey: ["ds-cal-util", start],
    queryFn: () => axios.get(`${API}/utilization`, { params: start ? { start } : {} }).then(r => r.data),
    retry: false,
    keepPreviousData: true,
  });
}

// iter145 · Fetch the current user just to gate the utilization panel visually.
function useMe() {
  return useQuery({
    queryKey: ["ds-cal-me"],
    queryFn: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`).then(r => r.data),
    staleTime: 60000,
  });
}

// ─── Day tile ───────────────────────────────────────────────────────────────

function FillBar({ booked, capacity }) {
  const pct = capacity > 0 ? Math.min(100, Math.round((booked / capacity) * 100)) : 0;
  const color = pct >= 90 ? "#ef4444" : pct >= 60 ? "#f3b14a" : "#22c55e";
  return (
    <div className="mt-1" title={`${booked}/${capacity || "—"}`}>
      <div
        style={{
          height: 4,
          borderRadius: 2,
          background: "rgba(148,163,184,0.20)",
          overflow: "hidden",
        }}
      >
        {capacity > 0 && (
          <div
            data-testid="ds-calendar-fill"
            style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 240ms ease" }}
          />
        )}
      </div>
      <div className="mt-1 flex items-baseline justify-between">
        <span style={mono(10, "var(--text, #f0f2f8)", 700, "0.4px")}>{booked}</span>
        <span style={mono(9, "var(--dimmer, #9198b0)")}>
          {capacity > 0 ? `/ ${capacity}` : "nincs idősáv"}
        </span>
      </div>
    </div>
  );
}

function DayPopup({ day }) {
  // Themed popup: uses CSS vars so it inverts correctly with the app theme.
  const style = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 6,
    background: "var(--bg-elevated, #14172200)",
    color: "var(--text, #0a0c10)",
    border: "1px solid var(--blue, #185FA5)",
    borderRadius: 6,
    boxShadow: "0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(24,95,165,0.10)",
    padding: 10,
    zIndex: 40,
    minWidth: 240,
    pointerEvents: "none",
  };
  return (
    <div data-testid={`ds-calendar-day-popup-${day.date}`} style={style}>
      <div className="mb-1.5" style={mono(9, undefined, 700, "0.4px")}>
        {DAY_NAMES_HU[day.day_of_week]} · {humanDate(day.date)}
      </div>
      {day.bookings.length === 0 && (
        <div style={mono(11, "var(--dimmer, #9198b0)")}>Nincs foglalás.</div>
      )}
      {day.bookings.map((b) => (
        <div
          key={b.id}
          className="flex items-start gap-2 py-1"
          style={{ borderTop: "1px dashed rgba(148,163,184,0.20)" }}
        >
          <span style={mono(11, "var(--blue, #185FA5)", 700, "0.4px")}>
            {b.slot_start_local}
          </span>
          <div className="flex-1 min-w-0">
            <div style={mono(11, undefined, 700)} className="truncate">{b.vonal || "—"}</div>
            <div style={mono(10, "var(--dimmer, #9198b0)")} className="truncate">
              {b.szolgaltato_neve || "—"}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DayTile({ day, isSelected, isToday, onSelect, onFillBarClick }) {
  const [hover, setHover] = useState(false);
  const pct = day.slot_capacity > 0 ? day.booked_count / day.slot_capacity : 0;

  return (
    <div
      data-testid={`ds-calendar-day-${day.date}`}
      data-selected={isSelected ? "true" : "false"}
      data-today={isToday ? "true" : "false"}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      className="relative rounded-md p-3 cursor-pointer"
      style={{
        background: isSelected ? "rgba(24,95,165,0.14)" : "var(--bg-surface, #0d0f18)",
        border: `1px solid ${isSelected ? BLUE : "rgba(255,255,255,0.10)"}`,
        outline: isToday ? `1px dashed rgba(24,95,165,0.55)` : "none",
        outlineOffset: -3,
        transition: "background 200ms ease, border-color 200ms ease",
      }}
    >
      <div className="flex items-baseline justify-between mb-1">
        <span style={syne(12, 700)}>{DAY_SHORT_HU[day.day_of_week]}</span>
        <span style={mono(10, "var(--dimmer, #9198b0)")}>{day.date.slice(5)}</span>
      </div>
      {isToday && (
        <div style={mono(8, BLUE, 700, "0.4px")} className="mb-1" data-testid={`ds-calendar-today-badge-${day.date}`}>
          MAI NAP
        </div>
      )}
      {/* Iter145 · B2 — clicking the fill bar deep-links to booking form
          filtered to THIS day's remaining free slots. Stop propagation so the
          tile-level onSelect (detail expansion) doesn't also fire. */}
      <div
        onClick={(e) => { e.stopPropagation(); onFillBarClick && onFillBarClick(day); }}
        data-testid={`ds-calendar-fillbar-${day.date}`}
        title="Foglalás erre a napra"
        style={{ cursor: "pointer" }}
      >
        <FillBar booked={day.booked_count} capacity={day.slot_capacity} />
      </div>
      {/* Peek at reception window (first configured) */}
      {day.reception_windows.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1" style={mono(9, "var(--dimmer, #9198b0)")}>
          <Clock3 size={10} />
          <span>
            {day.reception_windows[0].start_time}–{day.reception_windows[day.reception_windows.length - 1].end_time}
          </span>
        </div>
      )}
      {hover && day.bookings.length > 0 && <DayPopup day={day} />}
      {hover && day.bookings.length === 0 && day.slot_capacity > 0 && (
        <div
          data-testid={`ds-calendar-day-popup-${day.date}`}
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, marginTop: 6,
            background: "var(--bg-elevated)", color: "var(--text)",
            border: "1px solid rgba(148,163,184,0.30)", borderRadius: 6,
            boxShadow: "0 8px 20px rgba(0,0,0,0.25)", padding: 8,
            zIndex: 40, ...mono(11, "var(--dimmer, #9198b0)"),
          }}
        >
          Nincs foglalás erre a napra.
        </div>
      )}
    </div>
  );
}

// ─── Day detail (below the grid) ────────────────────────────────────────────

function DayDetail({ day, isDockAdmin }) {
  const qc = useQueryClient();
  const arrival = useMutation({
    mutationFn: ({ id, status }) => axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling/bookings/${id}/arrival`,
      { status },
    ).then(r => r.data),
    onSuccess: (data) => {
      const label = data.arrival_status === "late" ? "Késve érkezett" :
                    data.arrival_status === "arrived" ? "Érkezett" :
                    data.arrival_status === "no_show" ? "Nem jelent meg" : "Frissítve";
      toast.success(label);
      qc.invalidateQueries({ queryKey: ["ds-cal-week"] });
      qc.invalidateQueries({ queryKey: ["ds-cal-util"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba a jelöléskor"),
  });

  if (!day) return null;
  return (
    <div
      data-testid="ds-calendar-day-detail"
      className="mt-4 rounded-md p-4"
      style={{
        background: "var(--bg-elevated)",
        color: "var(--text, #0a0c10)",
        border: "1px solid rgba(148,163,184,0.20)",
      }}
    >
      <div className="flex items-baseline gap-3 mb-3">
        <div style={syne(16, 800)}>{DAY_NAMES_HU[day.day_of_week]}</div>
        <div style={mono(11, "var(--dimmer, #9198b0)")}>{humanDate(day.date)}</div>
        <div className="flex-1" />
        <div style={mono(10, "var(--blue, #185FA5)", 700, "0.4px")}>
          {day.booked_count} FOGLALT · {day.free_count} SZABAD
        </div>
      </div>

      {day.reception_windows.length === 0 && (
        <div className="mb-3 rounded p-2" style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.30)", ...mono(11, "#f3b14a") }}>
          Erre a napra nincs fogadási idősáv beállítva.
        </div>
      )}
      {day.reception_windows.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2" data-testid="ds-calendar-day-windows">
          {day.reception_windows.map((w, i) => (
            <span key={i} className="px-2 py-0.5 rounded"
              style={{ background: "rgba(24,95,165,0.10)", border: "1px solid rgba(24,95,165,0.30)", ...mono(10, "var(--blue, #185FA5)", 700, "0.4px") }}
            >{w.start_time}–{w.end_time}</span>
          ))}
        </div>
      )}

      {day.bookings.length === 0 ? (
        <div style={mono(12, "var(--dimmer, #9198b0)")}>Nincs foglalás erre a napra.</div>
      ) : (
        <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.25)" }}>
              <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>IDŐPONT</th>
              <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>REFERENCIA</th>
              <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>VONAL</th>
              <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>SZOLGÁLTATÓ</th>
              <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>RENDSZÁM</th>
              <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>IRÁNY</th>
              <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>ÉRKEZÉS</th>
              {isDockAdmin && <th className="py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}></th>}
            </tr>
          </thead>
          <tbody>
            {day.bookings.map((b) => (
              <tr key={b.id} data-testid={`ds-calendar-booking-row-${b.id}`}
                style={{ borderBottom: "1px solid rgba(148,163,184,0.10)" }}
              >
                <td className="py-2" style={mono(12, "var(--blue, #185FA5)", 700)}>
                  {b.slot_start_local}–{b.slot_end_local}
                </td>
                <td className="py-2" data-testid={`ds-calendar-ref-${b.id}`} style={mono(11, "var(--blue, #185FA5)", 800, "0.6px")}>
                  {b.reference || "—"}
                </td>
                <td className="py-2" style={mono(12)}>{b.vonal || "—"}</td>
                <td className="py-2" style={mono(12)}>{b.szolgaltato_neve || "—"}</td>
                <td className="py-2" style={mono(12)}>{b.rendszam || "—"}</td>
                <td className="py-2" style={mono(12)}>
                  {b.direction === "inbound" ? "Bejövő" : b.direction === "outbound" ? "Kimenő" : "—"}
                </td>
                <td className="py-2">
                  <ArrivalPill status={b.arrival_status} testId={`ds-arrival-status-${b.id}`} />
                </td>
                {isDockAdmin && (
                  <td className="py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        data-testid={`ds-arrival-arrived-${b.id}`}
                        title="Érkezett"
                        onClick={() => arrival.mutate({ id: b.id, status: "arrived" })}
                        disabled={arrival.isPending}
                        className="p-1 rounded"
                        style={{ background: "transparent", border: 0, color: "#22c55e", cursor: "pointer" }}
                      ><CheckCircle2 size={14} /></button>
                      <button
                        type="button"
                        data-testid={`ds-arrival-noshow-${b.id}`}
                        title="Nem jelent meg"
                        onClick={() => window.confirm("Nem jelent meg jelölést rögzítesz?") && arrival.mutate({ id: b.id, status: "no_show" })}
                        disabled={arrival.isPending}
                        className="p-1 rounded"
                        style={{ background: "transparent", border: 0, color: "#f87171", cursor: "pointer" }}
                      ><XCircle size={14} /></button>
                      {["arrived", "late", "no_show"].includes(b.arrival_status) && (
                        <button
                          type="button"
                          data-testid={`ds-arrival-reset-${b.id}`}
                          title="Visszaállít"
                          onClick={() => arrival.mutate({ id: b.id, status: "pending" })}
                          disabled={arrival.isPending}
                          className="p-1 rounded"
                          style={{ background: "transparent", border: 0, color: "var(--dimmer)", cursor: "pointer" }}
                        ><RotateCcw size={12} /></button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function DockCalendar() {
  const nav = useNavigate();
  const [start, setStart] = useState(() => mondayOf(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);
  const { data, isLoading, isError, error } = useWeek(start);
  const { data: me } = useMe();
  const { data: util } = useUtilization(start);

  const todayIso = useMemo(() => isoDate(new Date()), []);
  const days = data?.days || [];
  const selectedDay = useMemo(() => {
    if (!selectedDate) return null;
    return days.find(d => d.date === selectedDate) || null;
  }, [days, selectedDate]);

  const rangeLabel = data
    ? `${humanDate(data.week_start)} – ${humanDate(data.week_end)}`
    : "";

  // Iter145 · B2 — fill-bar deep-link handler. Books into the caller's OWN
  // plant (they're viewing their own calendar). Passes date; the book page
  // filters to that day's free slots.
  const handleFillBarClick = (day) => {
    if (day.slot_capacity > 0 && day.free_count === 0) {
      // Fully booked → let the user know rather than dropping them into an
      // empty picker.
      window.alert("Nincs szabad időablak erre a napra.");
      return;
    }
    const plantId = me?.tenant_id || "";
    const q = new URLSearchParams({ date: day.date });
    if (plantId) q.set("plant", plantId);
    nav(`/dock-scheduling/book?${q.toString()}`);
  };

  const isDockAdmin = me?.role === "platform_admin"
    || (Array.isArray(me?.dock_roles) && me.dock_roles.includes("dock_admin"));

  return (
    <div data-testid="ds-calendar" className="p-1">
      <header className="mb-5">
        <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
          <CalendarClock size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
          Időkapu-foglaló · Naptár
        </h1>
        <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
          Heti nézet · foglalások plant-lokál időben ({data?.timezone || "Europe/Budapest"})
        </div>
      </header>

      {/* Week nav */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="ds-calendar-week-nav-prev"
          onClick={() => setStart(s => addDays(s, -7))}
          className="px-2.5 py-1.5 rounded inline-flex items-center gap-1"
          style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text, #f0f2f8)", cursor: "pointer", ...mono(11, undefined, 700, "0.4px") }}
        ><ChevronLeft size={14} /> ELŐZŐ HÉT</button>

        <button
          type="button"
          data-testid="ds-calendar-week-nav-today"
          onClick={() => { setStart(mondayOf(new Date())); setSelectedDate(null); }}
          className="px-3 py-1.5 rounded"
          style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
        >MAI HÉT</button>

        <button
          type="button"
          data-testid="ds-calendar-week-nav-next"
          onClick={() => setStart(s => addDays(s, 7))}
          className="px-2.5 py-1.5 rounded inline-flex items-center gap-1"
          style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text, #f0f2f8)", cursor: "pointer", ...mono(11, undefined, 700, "0.4px") }}
        >KÖVETKEZŐ HÉT <ChevronRight size={14} /></button>

        <div className="flex-1" />
        <div data-testid="ds-calendar-range" style={mono(11, "var(--dimmer, #9198b0)")}>{rangeLabel}</div>
      </div>

      {/* Week grid */}
      {isError && (
        <div className="rounded p-3" style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", ...mono(11, "#ef4444") }}>
          {error?.response?.data?.detail || "Nem sikerült betölteni a naptárat."}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="ds-calendar-grid">
        {isLoading && Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="rounded-md p-3" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.06)", height: 96 }}>
            <div style={mono(10, "var(--dimmer, #9198b0)")}>…</div>
          </div>
        ))}
        {!isLoading && days.map(d => (
          <DayTile
            key={d.date}
            day={d}
            isSelected={selectedDate === d.date}
            isToday={d.date === todayIso}
            onSelect={() => setSelectedDate(d.date === selectedDate ? null : d.date)}
            onFillBarClick={handleFillBarClick}
          />
        ))}
      </div>

      {/* Iter145 · B3 — Weekly utilization panel (dock_admin only). Silently
          absent for pure bookers (backend 403s and useQuery has retry disabled). */}
      {isDockAdmin && util && (
        <section
          data-testid="ds-calendar-utilization"
          className="mt-4 rounded-md p-4"
          style={{ background: "var(--bg-elevated)", color: "var(--text)", border: "1px solid rgba(24,95,165,0.30)" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Activity size={14} color={BLUE} />
            <div style={syne(13, 700)}>Heti kihasználtság</div>
            <div className="flex-1" />
            <div style={mono(10, "var(--dimmer, #9198b0)")}>
              {humanDate(util.week_start)} – {humanDate(util.week_end)}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div style={mono(9, "var(--dimmer, #9198b0)", 700, "0.4px")}>KIHASZNÁLTSÁG</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span data-testid="ds-util-overall-pct" style={{ ...syne(28, 800), color: BLUE }}>{util.overall_pct}%</span>
                <span style={mono(11, "var(--dimmer)")}>{util.total_booked}/{util.total_capacity} slot</span>
              </div>
            </div>
            <div>
              <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>LEGELFOGLALTABB NAP</div>
              <div className="mt-1 flex items-baseline gap-2">
                <TrendingUp size={14} color={BLUE} />
                <span data-testid="ds-util-busiest" style={syne(14, 700)}>
                  {util.busiest_day_label || "—"}
                </span>
                <span style={mono(11, "var(--dimmer)")}>{util.busiest_day_booked} foglalás</span>
              </div>
            </div>
            <div>
              <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>KÉSŐI LEMONDÁS</div>
              <div className="mt-1 flex items-baseline gap-2">
                <XCircle size={14} color={util.late_cancel_count > 0 ? "#f87171" : "#22c55e"} />
                <span data-testid="ds-util-late-cancels" style={{ ...syne(20, 800), color: util.late_cancel_count > 0 ? "#f87171" : "#22c55e" }}>
                  {util.late_cancel_count}
                </span>
                <span style={mono(11, "var(--dimmer)")}>
                  {util.late_cancel_count === 0 ? "nincs" : "előfoglalási időn belül"}
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Day detail */}
      <DayDetail day={selectedDay} isDockAdmin={isDockAdmin} />

      {/* Legend / meta */}
      <div className="mt-4 flex flex-wrap gap-4" style={mono(10, "var(--dimmer, #9198b0)")}>
        <div className="inline-flex items-center gap-1.5"><Circle size={8} fill="#22c55e" color="#22c55e" /> &lt; 60% telített</div>
        <div className="inline-flex items-center gap-1.5"><Circle size={8} fill="#f3b14a" color="#f3b14a" /> 60–90%</div>
        <div className="inline-flex items-center gap-1.5"><Circle size={8} fill="#ef4444" color="#ef4444" /> ≥ 90%</div>
      </div>
    </div>
  );
}
